import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Navigation } from "./components/navigation";
import { Header } from "./components/header";
import { Features } from "./components/features";
import { About } from "./components/about";
import { Services } from "./components/services";
import { Gallery } from "./components/gallery";
import { Testimonials } from "./components/testimonials";
import { Contact } from "./components/contact";
import { ServiceDetailPage } from "./components/serviceDetailPage";
import { ServiceAreas } from "./components/serviceAreas";
import { ServiceAreaDetailPage } from "./components/serviceAreaDetailPage";
import { LocalSeoContent } from "./components/localSeoContent";
import { NotFound } from "./components/notFound";
import { ScrollToTop } from "./components/scrollToTop";
import { SEO } from "./components/seo";
import JsonData from "./data/data.json";
import { servicePages } from "./data/servicePages";
import { serviceAreas } from "./data/serviceAreas";
import { homeFaq } from "./data/homeFaq";
import "./App.css";

const SITE_URL = "https://clintonncpressurewashing.com";

// This is a service-area business: the Google Business Profile does not show a
// street address, so the site must not publish one either. Only the city and
// region are stated, which is what a hidden-address profile supports.
const BUSINESS_ADDRESS = {
  "@type": "PostalAddress",
  addressLocality: "Clinton",
  addressRegion: "NC",
  postalCode: "28328",
  addressCountry: "US",
};

const BUSINESS_GEO = {
  "@type": "GeoCoordinates",
  latitude: "34.9860918",
  longitude: "-78.387517",
};

// Matches the Google Business Profile hours exactly. The old markup claimed
// Mo-Su 00:00-23:59 (open 24/7), which contradicted the profile.
const BUSINESS_HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    opens: "07:00",
    closes: "20:00",
  },
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: "Sunday",
    opens: "14:00",
    closes: "19:00",
  },
];

const App = () => {
  const landingPageData = JsonData;
  const contactData = landingPageData.Contact || {};
  const testimonials = landingPageData.Testimonials || [];

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "@id": `${SITE_URL}/#business`,
    name: "Hamilton Pressure Washing",
    url: SITE_URL,
    telephone: "+1-910-990-1974",
    email: contactData.email,
    address: BUSINESS_ADDRESS,
    geo: BUSINESS_GEO,
    image: `${SITE_URL}/img/HamiltonFinalRebuild.png`,
    logo: `${SITE_URL}/img/HamiltonFinalRebuild.png`,
    priceRange: "$$",
    currenciesAccepted: "USD",
    openingHoursSpecification: BUSINESS_HOURS,
    description:
      "Hamilton Pressure Washing provides pressure washing, soft washing, house washing, roof washing, driveway cleaning, concrete cleaning, gutter cleaning, window cleaning, commercial pressure washing, and fleet washing in Clinton, NC and Sampson County.",
    areaServed: [
      {
        "@type": "City",
        name: "Clinton",
        addressRegion: "NC",
      },
      {
        "@type": "AdministrativeArea",
        name: "Sampson County, NC",
      },
      ...serviceAreas
        .filter((serviceArea) => serviceArea.slug !== "clinton-nc")
        .map((serviceArea) => ({
          "@type": "City",
          name: `${serviceArea.city}, ${serviceArea.state}`,
        })),
    ],
    makesOffer: servicePages.map((service) => ({
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: service.name,
        url: `${SITE_URL}/services/${service.slug}`,
      },
    })),
    hasMap: contactData.google,
    sameAs: [
      contactData.facebook,
      contactData.google,
      contactData.instagram || contactData.youtube,
    ].filter(Boolean),
  };

  const homeFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: homeFaq.map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: entry.answer,
      },
    })),
  };

  // Real customer testimonials, marked up as Review nodes attached to the same
  // business @id. No reviewRating or aggregateRating is emitted: these
  // testimonials carry no star values, and inventing them would be fabricated
  // markup. Star ratings in search results come from the Google Business
  // Profile, not from self-hosted review markup, which Google ignores for
  // LocalBusiness rich results.
  const testimonialsSchema = testimonials.length
    ? {
        "@context": "https://schema.org",
        "@type": "HomeAndConstructionBusiness",
        "@id": `${SITE_URL}/#business`,
        name: "Hamilton Pressure Washing",
        url: SITE_URL,
        address: BUSINESS_ADDRESS,
        telephone: "+1-910-990-1974",
        review: testimonials.map((testimonial) => ({
          "@type": "Review",
          author: {
            "@type": "Person",
            name: testimonial.name,
          },
          reviewBody: testimonial.text,
          itemReviewed: {
            "@type": "HomeAndConstructionBusiness",
            "@id": `${SITE_URL}/#business`,
            name: "Hamilton Pressure Washing",
          },
        })),
      }
    : null;

  const servicesListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Pressure Washing Services in Clinton, NC",
    itemListElement: servicePages.map((service, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: service.name,
      url: `${SITE_URL}/services/${service.slug}`,
    })),
  };

  const serviceAreasListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Pressure Washing Service Areas",
    itemListElement: serviceAreas.map((serviceArea, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: `${serviceArea.city}, ${serviceArea.state}`,
      url: `${SITE_URL}/service-areas/${serviceArea.slug}`,
    })),
  };

  return (
    <Router>
      <ScrollToTop />
      <Navigation />
      <Routes>
        <Route
          path="/"
          element={
            <>
              <SEO
                title="Pressure Washing Clinton NC | Hamilton Pressure Washing"
                description="Hamilton Pressure Washing provides professional pressure washing, soft washing, house washing, roof washing, driveway cleaning, and commercial exterior cleaning in Clinton, NC and Sampson County."
                schemaData={[businessSchema, homeFaqSchema]}
              />
              <Header data={landingPageData.Header} />
              <Features data={landingPageData.Features} />
              <LocalSeoContent />
            </>
          }
        />
        <Route
          path="/about"
          element={
            <>
              <SEO
                title="About Hamilton Pressure Washing | Local Exterior Cleaning Experts"
                description="Learn about Hamilton Pressure Washing, a locally owned pressure washing and soft washing company serving Clinton, NC."
              />
              <About data={landingPageData.About} />
            </>
          }
        />
        <Route
          path="/services"
          element={
            <>
              <SEO
                title="Pressure Washing Services Clinton NC | House, Roof, Driveway & More"
                description="Explore pressure washing services in Clinton, NC including house washing, driveway cleaning, roof washing, gutter cleaning, window cleaning, and commercial pressure washing."
                schemaData={servicesListSchema}
              />
              <Services data={landingPageData.Services} />
            </>
          }
        />
        <Route path="/services/:slug" element={<ServiceDetailPage />} />
        <Route
          path="/service-areas"
          element={
            <>
              <SEO
                title="Pressure Washing Service Areas | Clinton NC & Surrounding Towns"
                description="Hamilton Pressure Washing serves Clinton, NC, Sampson County, Roseboro, Newton Grove, Salemburg, Garland, Turkey, Warsaw, and nearby communities."
                schemaData={serviceAreasListSchema}
              />
              <ServiceAreas />
            </>
          }
        />
        <Route path="/service-areas/:slug" element={<ServiceAreaDetailPage />} />
        <Route
          path="/gallery"
          element={
            <>
              <SEO
                title="Pressure Washing Gallery Clinton NC | Before and After Results"
                description="See before-and-after exterior cleaning results from Hamilton Pressure Washing in Clinton, NC."
              />
              <Gallery data={landingPageData.Gallery} />
            </>
          }
        />
        <Route
          path="/testimonials"
          element={
            <>
              <SEO
                title="Pressure Washing Reviews Clinton NC | Customer Testimonials"
                description="Read what homeowners and businesses in Clinton, NC say about Hamilton Pressure Washing."
                schemaData={testimonialsSchema}
              />
              <Testimonials data={landingPageData.Testimonials} />
            </>
          }
        />
        <Route
          path="/contact"
          element={
            <>
              <SEO
                title="Contact Hamilton Pressure Washing | Free Quote Clinton NC"
                description="Contact Hamilton Pressure Washing for a free quote on house washing, roof cleaning, driveway cleaning, and exterior cleaning in Clinton, NC."
                schemaData={businessSchema}
              />
              <Contact data={landingPageData.Contact} />
            </>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

export default App;
